// 2026-10-27 coder(lq): 从真实菜单身份还原系统树，不使用权限策略的虚拟“未分组”层或接口前缀推断系统。
export const menuIdentity = menu => `${menu.authority}:${menu.service_id}:${menu.resource_id}`
const parentIdentity = menu => `${menu.authority}:${menu.parentId}`
const ownIdentity = menu => `${menu.authority}:${menu.resource_id}`
const validMenu = menu => ['baseservice', 'cms-native'].includes(menu.authority)
    && /^[A-Za-z0-9_-]{1,64}$/.test(menu.service_id || '') && Number.isInteger(menu.resource_id)
    && menu.resource_id > 0 && menu.resource_id <= 2147483647
// 2026-10-28 coder(lq): 导航接口的Long字段可能以数字或数字字符串返回，统一成非负整数，避免字符串计数被拼接到页面。
const relationCount = value => {
    const count = typeof value === 'number' ? value : Number(String(value ?? '').trim())
    return Number.isFinite(count) && count >= 0 ? Math.trunc(count) : 0
}

export function policyCatalog(root) {
    const menus = new Map()
    const visit = node => {
        if (!node || (node.kind || '').startsWith('INVALID')) return
        if (node.menu) {
            const menu = node.menu
            if (!validMenu(menu) || !Number.isInteger(menu.parentId)) throw new Error('菜单身份或父级信息不完整，请刷新或升级菜单导航接口')
            const id = ownIdentity(menu), previous = menus.get(id)
            if (previous && ['service_id', 'parentId', 'status', 'menuName'].some(field => previous.menu[field] !== menu[field])) {
                throw new Error('菜单身份出现冲突，暂不能修改系统范围')
            }
            // 2026-10-27 coder(lq): 同一菜单的多个策略投影只保留一份；使用直接计数，不重复累计祖先汇总。
            if (!previous || node.kind === 'MENU') menus.set(id, { key: menuIdentity(menu), title: menu.menuName || node.title,
                menu, count: relationCount(node.directCount), disabled: menu.status !== 1, children: [] })
        }
        ;(node.children || []).forEach(visit)
    }
    visit(root)
    const scopes = new Map()
    const invalid = { key: 'INVALID_HIERARCHY', title: '层级异常菜单（只读）', tree: [], keys: [], count: 0, incomplete: true }
    for (const item of menus.values()) {
        let top = item, visited = new Set(), issue = ''
        while (top.menu.parentId !== 0) {
            if (visited.has(top.key)) { issue = '父级存在循环'; break }
            visited.add(top.key)
            const parent = menus.get(parentIdentity(top.menu))
            if (!parent) { issue = '父级未完整读取'; break }
            top = parent
        }
        // 2026-10-28 coder(lq): 历史自循环/缺父级只隔离相关菜单及后代，平铺只读防递归；不猜归属、不改旧策略、不阻断正常系统。
        if (issue) {
            item.hierarchyIssue = `菜单 ${item.menu.resource_id}（${item.title}）${issue}：异常节点 ${top.menu.resource_id}，父级ID ${top.menu.parentId}。请在菜单管理核对；此处不自动修改菜单或已有策略。`
            invalid.tree.push(item)
            if (!item.disabled) { invalid.keys.push(item.key); invalid.count += item.count }
            continue
        }
        const cms = item.menu.authority === 'cms-native'
        const key = cms ? 'CMS' : `BASE_${top.menu.resource_id}`
        if (!scopes.has(key)) scopes.set(key, { key, title: cms ? 'CMS系统' : top.title, tree: [], keys: [], count: 0,
            incomplete: false })
        const scope = scopes.get(key), parent = menus.get(parentIdentity(item.menu))
        if (parent) parent.children.push(item)
        else scope.tree.push(item)
        if (!item.disabled) { scope.keys.push(item.key); scope.count += item.count }
    }
    return [...scopes.values(), ...(invalid.tree.length ? [invalid] : [])]
}

export function menuBinding(policy, key) {
    const entries = Object.entries(policy.systems)
    const exact = entries.find(([, spec]) => (spec.menus || []).includes(key))
    const service = entries.find(([, spec]) => (spec.services || []).includes(key.slice(0, key.lastIndexOf(':'))))
    const found = exact || service
    return found ? { name: found[0], mode: found[1].mode || 'OFF' } : { name: '', mode: 'OFF' }
}

export function scopeSelection(scope, policy) {
    const bindings = scope.keys.map(key => ({ key, ...menuBinding(policy, key) }))
    const active = bindings.filter(item => item.mode !== 'OFF')
    const modes = new Set(active.map(item => item.mode))
    const exclusionName = new RegExp(`^MENU_EXCLUDED_${scope.key}(?:_[1-9][0-9]*)?$`)
    // 2026-10-27 coder(lq): 同系统已有显式OFF覆盖时，不能把它显示为已启用，否则调整其他菜单会误开启该项。
    const chosen = active.length ? active : bindings.filter(item => item.name && !exclusionName.test(item.name))
    return { keys: chosen.map(item => item.key),
        mode: modes.size > 1 ? 'MIXED' : active.length ? active[0].mode : 'OFF' }
}

// 2026-10-27 coder(lq): 只替换用户正在编辑系统的精确菜单。旧服务绑定、未读取菜单及其他系统保持原值；
// 未勾选但继承旧服务规则的菜单必须显式OFF，避免“看似取消、实际仍拦截”。不会新增接口关系或继承授权。
export function updateScope(policy, scope, selection) {
    if (scope.incomplete) throw new Error('当前系统父级未完整读取，请先核对菜单层级')
    if (!['OFF', 'AUDIT', 'ENFORCE'].includes(selection.mode)) throw new Error('请先为当前系统选择统一的运行方式')
    const keys = Array.from(new Set(selection.keys)), available = new Set(scope.keys)
    if (keys.some(key => !available.has(key))) throw new Error('选择包含当前系统以外或失效的菜单，请刷新')
    if (selection.mode !== 'OFF' && !keys.length) throw new Error('请先勾选需要校验的菜单')
    const systems = new Map()
    for (const [name, spec] of Object.entries(policy.systems)) {
        const remaining = (spec.menus || []).filter(key => !available.has(key))
        const changed = remaining.length !== (spec.menus || []).length
        if (!changed) systems.set(name, spec)
        else if (remaining.length || (spec.services || []).length) systems.set(name, { ...spec, menus: remaining })
    }
    const add = (prefix, menus, mode) => {
        if (!menus.length) return
        const base = `${prefix}${scope.key}`
        let name = base, index = 1
        while (systems.has(name)) name = `${base}_${index++}`
        systems.set(name, { mode, services: [], menus: [...menus].sort() })
    }
    const selected = new Set(keys)
    const remainingPolicy = { ...policy, systems: Object.fromEntries(systems) }
    const exclusions = scope.keys.filter(key => !selected.has(key) && menuBinding(remainingPolicy, key).mode !== 'OFF')
    add('MENU_SCOPE_', keys, selection.mode)
    add('MENU_EXCLUDED_', exclusions, 'OFF')
    const result = { ...policy, systems: Object.fromEntries(systems) }
    validateSelectionPolicy(result)
    return result
}

export function validateSelectionPolicy(policy) {
    const menus = new Set(), services = new Set(), entries = Object.entries(policy.systems)
    if (entries.length > 64 || JSON.stringify(policy).length > 131072) throw new Error('系统策略超过服务端容量限制，请先整理已有分组')
    for (const [name, spec] of entries) {
        if (!spec || typeof spec !== 'object' || Array.isArray(spec)
            || Object.keys(spec).some(field => !['mode', 'services', 'menus'].includes(field))) throw new Error('已有系统策略格式不正确')
        if (!/^[A-Za-z0-9_-]{1,64}$/.test(name) || !['OFF', 'AUDIT', 'ENFORCE'].includes(spec.mode || 'OFF')) throw new Error('已有系统策略格式不正确')
        for (const [field, seen, pattern] of [['menus', menus, /^(baseservice|cms-native):[A-Za-z0-9_-]{1,64}:[1-9][0-9]{0,9}$/],
            ['services', services, /^(baseservice|cms-native):[A-Za-z0-9_-]{1,64}$/]]) {
            if (spec[field] !== undefined && !Array.isArray(spec[field])) throw new Error('已有菜单分组格式不正确')
            const values = spec[field] || []
            if (values.length > 8192) throw new Error('单系统菜单数量超过服务端限制')
            values.forEach(value => {
                if (typeof value !== 'string' || !pattern.test(value) || seen.has(value) || (field === 'menus' && Number(value.split(':').pop()) > 2147483647)) {
                    throw new Error('已有菜单分组格式不正确或存在重复绑定')
                }
                seen.add(value)
            })
        }
        if (spec.mode && spec.mode !== 'OFF' && !(spec.menus || []).length && !(spec.services || []).length) throw new Error('启用的系统必须选择菜单')
    }
    return policy
}
